import React from "react";
import NavigationBar from "./NavigationBar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LoadingScreen from "./ui/loading-screen";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import ShareDialog from "./ui/share-dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import NowPlaying from "./AudioPlayer/NowPlaying";
import { useToast } from "@/components/ui/use-toast";
import {
  Play,
  Pause,
  Heart,
  Share2,
  SkipBack,
  SkipForward,
} from "lucide-react";

const ProgressBar = ({ currentTime = 0, duration = 0, onSeek }) => {
  return (
    <div className="w-full">
      <Slider
        value={[currentTime]}
        max={duration}
        step={1}
        onValueChange={onSeek}
        className="w-full [&_[role=slider]]:bg-white [&_[role=slider]]:border-white [&_[role=slider]]:shadow-none [&_[role=slider]]:ring-white [&_[role=slider]]:ring-offset-0 [&_.relative]:bg-white/30 [&_[role=slider]]:hover:border-white/80 [&_[role=slider]]:focus:border-white/80 [&_[role=slider]]:focus:ring-white/80 [&_[data-filled]]:bg-white [&_[data-filled]]:opacity-100"
      />
      <div className="flex justify-between text-xs text-gray-700 mt-1">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const Layout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    currentEpisode,
    isPlaying,
    currentTime,
    duration,
    isFavorited,
    togglePlay,
    seek,
    toggleFavorite,
    queueEpisodes,
    setQueueEpisodes,
    playEpisode,
    historyEpisodes,
    setHistoryEpisodes,
    feedEpisodes,
    displayedEpisodes,
    setDisplayedEpisodes,
  } = useAudioPlayer();
  const [showShareDialog, setShowShareDialog] = React.useState(false);

  if (loading) return <LoadingScreen />;

  const handleNextTrack = () => {
    console.log("handleNextTrack called");
    console.log("Initial state:");
    console.log("- Current Episode:", currentEpisode?.title);
    console.log(
      "- Queue Episodes:",
      queueEpisodes.map((ep) => ep.title),
    );
    console.log(
      "- Display Episodes:",
      displayedEpisodes.map((ep) => ep.title),
    );
    console.log(
      "- History Episodes:",
      historyEpisodes.map((ep) => ep.title),
    );
    console.log(
      "- Feed Episodes:",
      feedEpisodes.map((ep) => ep.title),
    );

    if (queueEpisodes.length > 0) {
      console.log("\nQueue is not empty, processing next track...");
      const [nextEpisode, ...remainingQueue] = queueEpisodes;
      console.log("- Next Episode to play:", nextEpisode.title);
      console.log(
        "- Remaining Queue:",
        remainingQueue.map((ep) => ep.title),
      );

      // Find a new episode from feed that isn't in queue, history, or currently playing
      const newEpisode = feedEpisodes.find(
        (ep) =>
          !remainingQueue.some((qEp) => qEp.id === ep.id) &&
          !historyEpisodes.some((hEp) => hEp.id === ep.id) &&
          !displayedEpisodes.some((dEp) => dEp.id === ep.id) &&
          currentEpisode?.id !== ep.id &&
          nextEpisode.id !== ep.id,
      );

      // Update queue with remaining episodes plus the new episode if found
      const newQueue = newEpisode
        ? [...remainingQueue, newEpisode]
        : remainingQueue;

      setQueueEpisodes(newQueue);
      console.log("Updated queue episodes");

      // Update displayed episodes to match the new queue
      if (newEpisode) {
        console.log("- Added new episode to queue:", newEpisode.title);
        setDisplayedEpisodes([...remainingQueue, newEpisode]);
        console.log("- Updated displayed episodes");
      } else {
        setDisplayedEpisodes(remainingQueue);
      }

      // Only add to history if we're not in history or liked views
      if (currentEpisode && !location.pathname.includes("/profile/")) {
        console.log(
          "\nAdding current episode to history:",
          currentEpisode.title,
        );
        setHistoryEpisodes((prev) => {
          const newHistory = [currentEpisode, ...prev].slice(0, 50);
          console.log(
            "- New History:",
            newHistory.map((ep) => ep.title),
          );
          return newHistory;
        });
      } else {
        console.log("\nSkipping history update - in profile view");
      }

      // Play the next episode as a normal play since user initiated it
      console.log("\nPlaying next episode:", nextEpisode.title);
      playEpisode(nextEpisode, false);
    } else {
      console.log("\nNo episodes in queue to play next");
    }
  };

  const handlePreviousTrack = () => {
    if (currentTime > 3) {
      seek([0]);
      return;
    }

    if (historyEpisodes.length > 0) {
      let previousIndex = 0;
      if (currentEpisode) {
        while (
          previousIndex < historyEpisodes.length &&
          historyEpisodes[previousIndex].id === currentEpisode.id
        ) {
          previousIndex++;
        }
      }

      if (previousIndex < historyEpisodes.length) {
        const prevEpisode = historyEpisodes[previousIndex];
        const newHistory = [...historyEpisodes.slice(previousIndex + 1)];

        // Only update history if we're not in history or liked views
        if (!location.pathname.includes("/profile/")) {
          setHistoryEpisodes(newHistory);
        }

        if (currentEpisode) {
          setQueueEpisodes((prev) => [currentEpisode, ...prev]);
        }

        // Play without recording to history if in profile views
        playEpisode(
          prevEpisode,
          false,
          location.pathname.includes("/profile/")
            ? { skipHistoryRecord: true }
            : undefined,
        );
      } else {
        seek([0]);
      }
    } else {
      seek([0]);
    }
  };

  const handleLikeClick = () => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to like episodes",
      });
      navigate("/login");
      return;
    }
    toggleFavorite();
  };

  // Hide audio player only on search, main profile pages, and authentication pages
  const shouldShowPlayer = ![
    "/search",
    "/profile",
    "/profile/edit",
    "/login",
    "/signup",
  ].includes(location.pathname);

  return (
    <div className="fixed inset-0 bg-[#8285c7] bg-gradient-to-b from-[#8285c7] via-[#a3b8d4] to-[#aad8dd] flex flex-col">
      <div
        className={`flex-1 overflow-hidden flex flex-col p-4 ${shouldShowPlayer ? "pb-48" : location.pathname.includes("/login") || location.pathname.includes("/signup") ? "pb-24" : "pb-16"}`}
      >
        {currentEpisode && !location.pathname.includes("/login") && !location.pathname.includes("/signup") && <NowPlaying />}
        <div className="flex-1 overflow-hidden flex items-center justify-center">
          <Outlet />
        </div>
      </div>

      {currentEpisode && shouldShowPlayer && (
        <div className="fixed bottom-14 left-0 right-0 z-50">
          <div className="backdrop-blur-xl px-4 py-4 rounded-t-xl bg-white/0 border-t border-l border-r border-white/20">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <ProgressBar
                  currentTime={currentTime}
                  duration={duration}
                  onSeek={seek}
                />
              </div>
            </div>
            <div className="flex items-center justify-center gap-8 mt-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLikeClick}
                className="hover:bg-white/10 text-gray-700"
              >
                <Heart
                  className={`h-6 w-6 ${isFavorited && user ? "fill-[#735fe2] text-[#735fe2]" : ""}`}
                />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePreviousTrack}
                className="text-gray-700 hover:bg-white/10"
              >
                <SkipBack className="h-6 w-6" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className={`h-12 w-12 rounded-full border-gray-700/20 text-gray-700 ${isPlaying ? "bg-gradient-to-r from-[#735fe2] to-[#627ae2] text-white border-transparent" : "bg-white/10 hover:bg-white/20"}`}
                onClick={togglePlay}
              >
                {isPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextTrack}
                className="text-gray-700 hover:bg-white/10"
              >
                <SkipForward className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowShareDialog(true)}
                className="text-gray-700 hover:bg-white/10"
              >
                <Share2 className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <NavigationBar className="fixed bottom-0 left-0 right-0 z-50" />

      {showShareDialog && currentEpisode && (
        <ShareDialog
          title={currentEpisode.title}
          url={currentEpisode.audioUrl}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
        />
      )}
    </div>
  );
};

export default Layout;
